import { Controller } from "@hotwired/stimulus"
import { syncSectionDiagram } from "../../helpers/section_diagram.js"

// Elements that already act on Enter themselves; hijacking it would swallow their click.
const SELF_HANDLING_TAGS = ["TEXTAREA", "BUTTON", "A", "SELECT"]

// Connects to data-controller="sanitation-forms--wizard-component"
export default class extends Controller {
  static targets = [
    "step",
    "threeStepNav",
    "twoStepNav",
    "nextButton",
    "backButton",
    "submitButton",
    "kindRadio",
    "percentageField",
    "samplesOpenRadio",
    "sectionsInput",
    "samplesInput",
    "diagramVariant",
    "diagramFallback",
    "kindVariant",
    "stepStack",
  ]

  static values = { step: { type: Number, default: 1 } }

  connect() {
    this.sync()
    this.lockStepStackHeight()
  }

  keydown(event) {
    if (event.key !== "Enter") return
    if (SELF_HANDLING_TAGS.includes(event.target.tagName)) return
    if (event.target.type === "submit") return
    if (this.onLastStep) return

    event.preventDefault()
    this.next()
  }

  next() {
    if (this.stepValue === 1 && this.isFindings) {
      this.stepValue = 3
    } else if (this.stepValue < 3) {
      this.stepValue += 1
    }
    this.sync()
    this.focusStep()
  }

  back() {
    if (this.stepValue === 3 && this.isFindings) {
      this.stepValue = 1
    } else if (this.stepValue > 1) {
      this.stepValue -= 1
    }
    this.sync()
    this.focusStep()
  }

  get selectedKind() {
    const checked = this.kindRadioTargets.find((radio) => radio.checked)
    return checked?.value || "sample"
  }

  get isFindings() {
    return this.selectedKind === "open_ended"
  }

  get samplesAreOpen() {
    return this.hasSamplesOpenRadioTarget && this.samplesOpenRadioTarget.checked
  }

  get totalSteps() {
    return this.isFindings ? 2 : 3
  }

  get visualStep() {
    return this.isFindings && this.stepValue === 3 ? 2 : this.stepValue
  }

  get onLastStep() {
    return this.visualStep >= this.totalSteps
  }

  get sectionCount() {
    const parsed = parseInt(this.hasSectionsInputTarget ? this.sectionsInputTarget.value : null, 10)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null
  }

  sync() {
    this.showStep()
    this.syncNav()
    this.syncBranchFields()
    this.syncSamplesField()
    this.updateDiagram()
    this.syncKindVariants()
  }

  showStep() {
    this.stepTargets.forEach((element) => {
      const active = Number(element.dataset.step) === this.stepValue
      element.classList.toggle("invisible", !active)
      element.toggleAttribute("inert", !active)
    })
  }

  lockStepStackHeight() {
    if (!this.hasStepStackTarget) return

    const height = this.stepStackTarget.getBoundingClientRect().height
    if (height > 0) this.stepStackTarget.style.minHeight = `${Math.round(height)}px`
  }

  focusStep() {
    const panel = this.stepTargets.find((element) => Number(element.dataset.step) === this.stepValue)
    if (!panel) return

    const field = panel.querySelector(
      "input[type='number']:not([disabled]), input[type='text']:not([disabled]), textarea:not([disabled])",
    )
    ;(field || panel).focus()
  }

  syncNav() {
    const findings = this.isFindings
    if (this.hasThreeStepNavTarget) {
      this.threeStepNavTarget.classList.toggle("hidden", findings)
    }
    if (this.hasTwoStepNavTarget) {
      this.twoStepNavTarget.classList.toggle("hidden", !findings)
    }

    this.backButtonTarget.classList.toggle("invisible", this.stepValue === 1)
    this.nextButtonTarget.classList.toggle("hidden", this.onLastStep)
    this.submitButtonTarget.classList.toggle("hidden", !this.onLastStep)

    this.updateIndicator(this.threeStepNavTarget, this.stepValue)
    this.updateIndicator(this.twoStepNavTarget, this.visualStep)
  }

  updateIndicator(nav, current) {
    nav.querySelectorAll("[data-indicator-step]").forEach((element) => {
      const step = Number(element.dataset.indicatorStep)
      element.classList.toggle("step-primary", step <= current)
      if (step === current) {
        element.setAttribute("aria-current", "step")
      } else {
        element.removeAttribute("aria-current")
      }
    })
  }

  syncBranchFields() {
    if (!this.hasPercentageFieldTarget) return

    const show = this.selectedKind === "sample"
    this.percentageFieldTarget.classList.toggle("hidden", !show)
    const input = this.percentageFieldTarget.querySelector("input")
    if (input) input.disabled = !show
  }

  syncSamplesField() {
    if (!this.hasSamplesInputTarget) return

    const open = this.samplesAreOpen
    this.samplesInputTarget.disabled = open
    if (open) this.samplesInputTarget.value = 1
  }

  updateDiagram() {
    syncSectionDiagram(this.diagramVariantTargets, this.diagramFallbackTargets, this.sectionCount)
  }

  syncKindVariants() {
    const kind = this.selectedKind
    this.kindVariantTargets.forEach((element) => {
      const modes = (element.dataset.modes || "").split(" ").filter(Boolean)
      const hide = modes.length > 0 && !modes.includes(kind)
      element.classList.toggle("invisible", hide)
      element.toggleAttribute("inert", hide)
    })
  }
}
